import { FC, ReactNode, useEffect, useState } from 'react';
import { Box, LinearProgress, alpha, lighten, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';

import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from 'src/utils/auth';
import { AuthMiddleware } from 'src/middlewares/AuthMiddleware';

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const { handleInitUser } = useAuth();
  const [authLoading, setAuthLoading] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  useEffect(() => {
    const authenticateUser = async () => {
      await handleInitUser();
      setAuthLoading(false)
    }

    authenticateUser();
  }, [])

  const handleMouseMove = (e: MouseEvent) => {
    // Mostrar sidebar quando o mouse estiver perto da borda esquerda (20px)
    if (e.clientX < 20) {
      setSidebarVisible(true);
    }
  };

  const handleMouseLeaveSidebar = () => {
    setSidebarVisible(false);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  if (authLoading) {
    return (
      <LinearProgress style={{ height: 3 }} />
    )
  }

  return (
    <AuthMiddleware>
      <Box
        sx={{
          flex: 1,
          height: '100%',

          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                  lighten(theme.colors.primary.main, 0.7),
                  0.15
                )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                  theme.colors.alpha.black[100],
                  0.1
                )}, 0px 5px 12px -4px ${alpha(
                  theme.colors.alpha.black[100],
                  0.05
                )}`
          }
        }}
      >
        <Header />
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            zIndex: theme.zIndex.drawer,
            transition: 'transform 0.3s ease-in-out',
            transform: sidebarVisible ? 'translateX(0)' : `translateX(-${theme.sidebar.width})`,
            '&:hover': {
              transform: 'translateX(0)'
            }
          }}
          onMouseLeave={handleMouseLeaveSidebar}
        >
          <Sidebar />
        </Box>
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'block',
            flex: 1,
            pt: `${theme.header.height}`,
            [theme.breakpoints.up('lg')]: {
              ml: sidebarVisible ? `${theme.sidebar.width}` : 0,
              transition: 'margin-left 0.3s ease-in-out'
            }
          }}
        >
          <Box display="block">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </AuthMiddleware>
  );
};

export default SidebarLayout;